import util from '../../lib/util';
import store from '../store';
import Actions from '../actions/indexExport';
import {getCurrentUserId} from '../../lib/picos/currentLogin';
import newHRTimestamp from '../../lib/picos/timestamp';
import {findAll} from "../find";
import Campaign from '../../entities/campaign';
import Lead from '../../entities/lead';
import Task from '../../entities/task';
import Document from '../../entities/document';
import Comment from '../../entities/comment';

/*
 * Delete/nullify anywhere this campaign is used in other entities
 *
 * @param {Campaign} campaign
 */
export function deleteCampaign(campaign: Campaign, mergeId?: string) {
    let targetImportSource = 'merge';

    if (!mergeId) {
        targetImportSource = 'delete';
        mergeId = campaign.id;
    }
    /* Simple delete usages */
        
    store.getState().leads
        .getMappingFor('campaignId', campaign.id)
        .filter(leadTemp => leadTemp.status !== 'D')
        .forEach(leadTemp => {
            const _lead = leadTemp.clone();
            _lead.status =  'U';
            _lead.addedOn = newHRTimestamp();
            _lead.importSource = targetImportSource;
            _lead.importId = mergeId;

            _lead.campaigns = _lead.campaigns?.filter(id => id !== campaign.id) || [];
            store.dispatch(Actions.LeadActions.saveLeadQueue(_lead));
        });

    /* Complex delete usages */
        /* - Delete embedded key/value usages */
    
    findAll<Task>('tasks')
        // Has to have the tieIns property
        .filter(taskTemp => taskTemp.status !== 'D' && taskTemp.hasOwnProperty('tieIns'))
        // Has to have a tieIns with a key/value pair that matches
        .filter(taskTemp => !!taskTemp.tieIns?.some(f => {
            return 'Campaign' === f.entity && f.id === campaign.id;
        }))
        // For each task, go through tieIns and delete entries with the key/value pair
        .forEach(taskTemp => {
            const _task = taskTemp.clone();
            _task.tieIns = [...(taskTemp.tieIns || [])]
                .filter(f => !('Campaign' === f.entity && f.id === campaign.id));
            _task.status = 'U';
            _task.addedOn = newHRTimestamp();
            _task.addedBy = getCurrentUserId();
            _task.importSource = targetImportSource;
            _task.importId = mergeId;
            store.dispatch(Actions.TaskActions.saveTaskQueue(_task));
        });

    /* Remove the actual campaign */
    campaign.status = 'D';
    campaign.addedOn = newHRTimestamp();
    campaign.addedBy = getCurrentUserId();
    campaign.importSource = targetImportSource;
    campaign.importId = mergeId;
    store.dispatch(Actions.CampaignActions.removeCampaignQueue(campaign));
    

    /* Delete any documents */
    findAll<Document>('documents')
         .filter(documentTemp => documentTemp.entityType === 'campaign' && documentTemp.entityId === campaign.id && documentTemp.status !== 'D')
         .forEach(documentTemp => {
            const _document = documentTemp.clone();
            // Delete the target
            _document.status = 'D';
            _document.addedOn = newHRTimestamp();
            _document.addedBy = getCurrentUserId();
            _document.importSource = targetImportSource;
            _document.importId = mergeId;
            store.dispatch(Actions.DocumentActions.removeDocumentQueue(_document));
         });
    

    /* Delete any comments */
    findAll<Comment>('comments')
         .filter(commentTemp => commentTemp.entityType === 'campaign' && commentTemp.entityId === campaign.id && commentTemp.status !== 'D')
         .forEach(commentTemp => {
            const _comment = commentTemp.clone();
            // Delete the target
            _comment.status = 'D';
            _comment.addedOn = newHRTimestamp();
            _comment.addedBy = getCurrentUserId();
            _comment.importSource = targetImportSource;
            _comment.importId = mergeId;
            store.dispatch(Actions.CommentActions.removeCommentQueue(_comment));
         });
    
}

/*
 * Replace anywhere this campaign is used in other entities.
 *
 * @param {Campaign} campaign
 * @param {Campaign} replaceWith
 * @return {Campaign} - Campaign will have refs stripped away
 */
export function replaceCampaign(campaign: Campaign, replaceWith: Campaign, mergeId?: string) {
    let targetImportSource = 'merge';

    if (!mergeId) {
        targetImportSource = 'delete';
        mergeId = campaign.id;
    }
    /* Simple replace usages */
        
    store.getState().leads
        .getMappingFor('campaignId', campaign.id)
        .filter(leadTemp => leadTemp.status !== 'D')
        .forEach(leadTemp => {
            const _lead = leadTemp.clone();
            _lead.status =  'U';
            _lead.addedOn = newHRTimestamp();
            _lead.importSource = targetImportSource;
            _lead.importId = mergeId;

            _lead.campaigns = _lead.campaigns?.map(id => (id === campaign.id) ? replaceWith.id : id) || [];
            store.dispatch(Actions.LeadActions.saveLeadQueue(_lead));
        });

    /* Complex replace usages */
        /* - Modify embedded key/value usages */
    
    findAll<Task>('tasks')
        // Has to have the tieIns property
        .filter(taskTemp => taskTemp.status !== 'D' && taskTemp.hasOwnProperty('tieIns'))
        // Has to have a tieIns with a key/value pair that matches
        .filter(taskTemp => !!taskTemp.tieIns?.some(f => {
            return 'Campaign' === f.entity && f.id === campaign.id;
        }))
        // For each task, go through tieIns and replace entries with the key/value pair
        .forEach(taskTemp => {
            const _task = taskTemp.clone();
            _task.tieIns = [...(taskTemp.tieIns || [])]
                .map(f => {
                        if('Campaign' === f.entity && f.id === campaign.id) {
                            const _f = {...f};
                            _f.id = replaceWith.id;
                            return _f;
                        }
                        else return f;
                    });
            _task.status = 'U';
            _task.addedOn = newHRTimestamp();
            _task.addedBy = getCurrentUserId();
            _task.importSource = targetImportSource;
            _task.importId = mergeId;
            store.dispatch(Actions.TaskActions.saveTaskQueue(_task));
        });

    /* Move any documents */
    findAll<Document>('documents')
         .filter(documentTemp => documentTemp.entityType === 'campaign' && documentTemp.entityId === campaign.id && documentTemp.status !== 'D')
         .forEach(documentTemp => {
            const _document = documentTemp.clone();
            // Replace the target with the new ID
			_document.entityId = replaceWith.id;
            _document.status = 'U';
            _document.addedOn = newHRTimestamp();
            _document.addedBy = getCurrentUserId();
            _document.importSource = targetImportSource;
            _document.importId = mergeId;
            store.dispatch(Actions.DocumentActions.saveDocumentQueue(_document));
         });
    

    /* Move any comments */
    findAll<Comment>('comments')
         .filter(commentTemp => commentTemp.entityType === 'campaign' && commentTemp.entityId === campaign.id && commentTemp.status !== 'D')
         .forEach(commentTemp => {
            const _comment = commentTemp.clone();
            // Replace the target with the new ID
			_comment.entityId = replaceWith.id;
            _comment.status = 'U';
            _comment.addedOn = newHRTimestamp();
            _comment.addedBy = getCurrentUserId();
            _comment.importSource = targetImportSource;
            _comment.importId = mergeId;
            store.dispatch(Actions.CommentActions.saveCommentQueue(_comment));
         });
    
    return campaign;
}

/*
 * Determine the impact of deleting this campaign from other entities
 *
 * @param {Campaign} campaign
 * @return {Array.<{count: number, entity: string}>} - a list of impacted entities, along with a count of impacted entities
 */
export function deleteImpactCampaign(campaign: Campaign) {
    const impacts: string[] = [];
    const state = store.getState();
    
    /* Simple delete usages */
        
    const leadCampaignsImpact = state.leads
          .getMappingFor('campaignId', campaign.id)
          .filter(lead => lead.status !== 'D');

    leadCampaignsImpact.forEach(() => impacts.push('leads'));

    /* Complex delete usages */
        /* - Impact embedded key/value usages */
    
    const taskTieInsImpact = findAll<Task>('tasks')
		.filter(task => task.status !== 'D' && task.hasOwnProperty('tieIns'))
		.filter(task => !!task.tieIns?.some(f => {
				return 'Campaign' === f.entity && f.id === campaign.id;
			}));
    taskTieInsImpact.forEach(() => impacts.push('tasks'));

    return util.countDistinct(impacts);
}